import React, { useMemo } from 'react';
import type {
    OnChangeValue,
    OptionProps,
    SelectComponentsConfig,
    StylesConfig,
} from 'react-select';
import Select, { components } from 'react-select';

// icons
import Done from '@mui/icons-material/Done';

import ComponentTypesIconWrapper from '../../../components/componentTypesIcon/ComponentTypesIconWrapper';
// components
import {
    SearchableSelectType,
    SelectOptionExtended,
} from '../../../components/inputs/searchableSelect';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

import useComponentTypesQuery from '../../../hooks/component-types/useComponentTypesQuery';

import {
    ComponentTypeMap,
    ComponentTypeMapped,
} from '../../../models/component-types-model';

interface VariantTypeStyledProps {
    variantColor: string;
    bgColor: string;
}

export const VariantTypeOption = (
    props: OptionProps<SearchableSelectType, false>,
) => {
    const { data: componentTypes } = useComponentTypesQuery();

    const componentType = useMemo(() => {
        return componentTypes?.map?.[props.data.value];
    }, [componentTypes, props]);

    const derivedSecondary = componentType?.secondary_color ?? '';

    return (
        <components.Option {...props}>
            <OptionContent>
                <OptionLabel
                    variantColor={componentType?.base_color ?? ''}
                    bgColor={derivedSecondary}
                >
                    {props.label}
                </OptionLabel>
                {props.isSelected && <OptionSelectedIcon />}
            </OptionContent>
        </components.Option>
    );
};

const VariantTypeOptionWithIcon = (
    props: OptionProps<SearchableSelectType, false>,
) => {
    const { data: componentTypes } = useComponentTypesQuery();

    const componentType = useMemo(() => {
        return componentTypes?.map?.[props.data.value];
    }, [componentTypes, props]);

    return (
        <components.Option {...props}>
            <Content isSelected={props.isSelected}>
                <ComponentTypesIconWrapper
                    dataKey={(componentType as ComponentTypeMapped)?.data_key}
                />
                <VariantText>{props.label}</VariantText>
            </Content>
        </components.Option>
    );
};

export const getCustomStyles = (
    componentTypesData: ComponentTypeMap,
): StylesConfig<SearchableSelectType, false> => ({
    container: (provided) => ({
        ...provided,
        right: 100,
    }),
    control: (provided) => ({
        ...provided,
        border: `none`,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        cursor: 'pointer',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: componentTypesData[state.data.value]?.base_color,
        background: componentTypesData[state.data.value]?.secondary_color,
        fontSize: '14px',
        fontWeight: 'bold',
        paddingTop: aampeTheme.spacing(0.25),
    }),
    menu: (provided) => ({
        ...provided,
        width: '230px',
        marginTop: aampeTheme.spacing(0),
    }),
    dropdownIndicator: (provided, state) => ({
        ...provided,
        padding: aampeTheme.spacing(0),
        color: componentTypesData[state.getValue()[0].value]?.base_color,
    }),
    option: (provided, state) => ({
        ...provided,
        padding: aampeTheme.spacing(1),
        backgroundColor:
            state.isFocused || state.isSelected
                ? aampeTheme.colors.gray25
                : aampeTheme.colors.white,
        '&:active': {
            backgroundColor: aampeTheme.colors.bordersWG01,
        },
    }),
});

interface VariantTypeDropdownProps {
    currentVariantType: string;
    variantId: string;
    formulaId: number;
    handleVariantChange: (
        variantId: string,
        variantType: string,
        componentId: string,
    ) => void;
    showOnlyOptions?: boolean;
    shouldConfirm?: boolean;
    handleMenuClose: () => void;
}

const VariantTypeDropdown: React.FC<
    React.PropsWithChildren<VariantTypeDropdownProps>
> = ({
    currentVariantType,
    variantId,
    handleVariantChange,
    showOnlyOptions,
    handleMenuClose,
}) => {
    const { data: componentTypes } = useComponentTypesQuery();

    const componentTypesData = useMemo(() => {
        return componentTypes?.map ?? {};
    }, [componentTypes]);

    const variantTypes = useMemo(
        () => componentTypes?.selectOptions ?? [],
        [componentTypes],
    );

    const initialValue = React.useMemo(
        () => ({ label: currentVariantType, value: currentVariantType }),
        [currentVariantType],
    );

    const handleChange = (
        newValue: OnChangeValue<SelectOptionExtended, false>,
    ) => {
        if (newValue && newValue !== initialValue) {
            const { value, componentTypeId } = newValue!;
            handleVariantChange(
                variantId,
                value as string,
                componentTypeId as string,
            );
        }
        handleMenuClose();
    };

    const components = useMemo(() => {
        const selectComponents: SelectComponentsConfig<
            SearchableSelectType,
            false,
            any
        > = {
            Option: VariantTypeOptionWithIcon,
            IndicatorSeparator: () => null,
        };
        if (showOnlyOptions) {
            selectComponents['Input'] = () => null;
            selectComponents['Control'] = () => null;
            selectComponents['SingleValue'] = () => null;
        }
        return selectComponents;
    }, [showOnlyOptions]);

    const customStyles = useMemo(
        () => getCustomStyles(componentTypesData),
        [componentTypesData],
    );

    return (
        <Select
            options={variantTypes}
            value={initialValue}
            onChange={handleChange}
            styles={customStyles}
            isSearchable={false}
            defaultMenuIsOpen={showOnlyOptions}
            components={components}
        />
    );
};

const OptionContent = styled.div(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(2),
}));

const OptionLabel = styled('label').withConfig({
    shouldForwardProp: (prop) => !['variantColor', 'bgColor'].includes(prop),
})<VariantTypeStyledProps>(({ variantColor, bgColor }) => ({
    cursor: 'pointer',
    color: variantColor,
    backgroundColor: bgColor,
    padding: aampeTheme.spacing(1),
    borderRadius: aampeTheme.borderRadius.default,
}));
const OptionSelectedIcon = styled(Done)`
    color: ${aampeTheme.colors.solidA01};
    margin-left: auto;
`;

const Content = styled.div<{ isSelected: boolean }>(({ isSelected }) => ({
    display: 'flex',
    padding: aampeTheme.spacing4(1, 2, 1, 2),
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    color: isSelected
        ? aampeTheme.colors.neutral09
        : aampeTheme.colors.textAccessibleWG01,
    backgroundColor: isSelected ? aampeTheme.colors.interactiveWG01 : 'inherit',
    cursor: isSelected ? 'default' : 'pointer',
    '&:hover': {
        backgroundColor: aampeTheme.colors.interactiveWG01,
        color: aampeTheme.colors.neutral09,
    },
}));

const VariantText = styled.div({
    fontSize: aampeTheme.size.font.default,
    lineHeight: '24px',
    fontWeight: 400,
});

export default VariantTypeDropdown;
